import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const TestContext = createContext();

const TEST_COOKIE_TO_LOCAL_STORAGE_MAP = {
  'cko-search': 'spaseekers:cko-search'
};

const canUseLocalStorage = () =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const getStoredVariation = (localStorageKey) => {
  if (!localStorageKey || !canUseLocalStorage()) {
    return undefined;
  }

  return window.localStorage.getItem(localStorageKey);
};

const getCookieValue = (cookieName) => {
  if (!cookieName || typeof document === 'undefined') {
    return undefined;
  }

  const escapedCookieName = cookieName.replace(
    /[.*+?^${}()|[\]\\]/g,
    '\\$&',
  );
  const cookieMatch = document.cookie.match(
    new RegExp(`(?:^|; )${escapedCookieName}=([^;]*)`),
  );

  if (!cookieMatch) {
    return undefined;
  }

  return decodeURIComponent(cookieMatch[1]);
};

export const TestProvider = ({
  children,
  cookieToLocalStorageMap = TEST_COOKIE_TO_LOCAL_STORAGE_MAP,
}) => {
  const [variations, setVariations] = useState({});

  const localStorageToCookieMap = useMemo(() => {
    return Object.entries(cookieToLocalStorageMap).reduce(
      (mappedValues, [cookieName, localStorageKey]) => {
        if (!cookieName || !localStorageKey) {
          return mappedValues;
        }

        return {
          ...mappedValues,
          [localStorageKey]: cookieName,
        };
      },
      {},
    );
  }, [cookieToLocalStorageMap]);

  const syncCookieMappedTestVariations = useCallback(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const nextVariations = {};
    Object.entries(cookieToLocalStorageMap).forEach(
      ([cookieName, localStorageKey]) => {
        const cookieValue = getCookieValue(cookieName);

        if (!cookieValue || !cookieValue.trim() || !localStorageKey) {
          return;
        }

        if (canUseLocalStorage()) {
          window.localStorage.setItem(localStorageKey, cookieValue);
        }

        nextVariations[localStorageKey] = cookieValue;
      },
    );

    if (!Object.keys(nextVariations).length) {
      return;
    }

    setVariations((currentVariations) => {
      let hasChange = false;
      Object.entries(nextVariations).forEach(([localStorageKey, value]) => {
        if (currentVariations[localStorageKey] !== value) {
          hasChange = true;
        }
      });

      if (!hasChange) {
        return currentVariations;
      }

      return {
        ...currentVariations,
        ...nextVariations,
      };
    });
  }, [cookieToLocalStorageMap]);

  const syncTestVariation = useCallback((localStorageKey) => {
    if (!localStorageKey) {
      return undefined;
    }

    const mappedCookieName = localStorageToCookieMap[localStorageKey];
    const mappedCookieValue = getCookieValue(mappedCookieName);

    if (mappedCookieValue && mappedCookieValue.trim()) {
      if (canUseLocalStorage()) {
        window.localStorage.setItem(localStorageKey, mappedCookieValue);
      }

      setVariations((currentVariations) => {
        if (currentVariations[localStorageKey] === mappedCookieValue) {
          return currentVariations;
        }

        return {
          ...currentVariations,
          [localStorageKey]: mappedCookieValue,
        };
      });

      return mappedCookieValue;
    }

    const storedValue = getStoredVariation(localStorageKey);
    setVariations((currentVariations) => {
      if (currentVariations[localStorageKey] === storedValue) {
        return currentVariations;
      }

      return {
        ...currentVariations,
        [localStorageKey]: storedValue,
      };
    });

    return storedValue;
  }, [localStorageToCookieMap]);

  const getTestVariation = useCallback(
    (localStorageKey) => {
      if (!localStorageKey) {
        return undefined;
      }

      if (Object.prototype.hasOwnProperty.call(variations, localStorageKey)) {
        return variations[localStorageKey];
      }

      return getStoredVariation(localStorageKey);
    },
    [variations],
  );

  const setTestVariation = useCallback((localStorageKey, value) => {
    if (!localStorageKey) {
      return;
    }

    if (canUseLocalStorage()) {
      window.localStorage.setItem(localStorageKey, value);
    }

    setVariations((currentVariations) => ({
      ...currentVariations,
      [localStorageKey]: value,
    }));
  }, []);

  const clearTestVariation = useCallback((localStorageKey) => {
    if (!localStorageKey) {
      return;
    }

    if (canUseLocalStorage()) {
      window.localStorage.removeItem(localStorageKey);
    }

    setVariations((currentVariations) => ({
      ...currentVariations,
      [localStorageKey]: null,
    }));
  }, []);

  useEffect(() => {
    syncCookieMappedTestVariations();
  }, [syncCookieMappedTestVariations]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleStorageChange = ({ key }) => {
      if (key) {
        syncTestVariation(key);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [syncTestVariation]);

  const contextValue = useMemo(
    () => ({
      getTestVariation,
      setTestVariation,
      clearTestVariation,
      syncTestVariation,
      syncCookieMappedTestVariations,
    }),
    [
      getTestVariation,
      setTestVariation,
      clearTestVariation,
      syncTestVariation,
      syncCookieMappedTestVariations,
    ],
  );

  return (
    <TestContext.Provider value={contextValue}>{children}</TestContext.Provider>
  );
};

const useTestVariationContext = () => {
  const context = useContext(TestContext);

  if (!context) {
    throw new Error('useTestVariationContext must be used within a TestProvider');
  }

  return context;
};

const useGetTestVariation = () => {
  const { getTestVariation } = useTestVariationContext();

  return getTestVariation;
};

const useSetTestVariation = () => {
  const { setTestVariation } = useTestVariationContext();

  return setTestVariation;
};

const useClearTestVariation = () => {
  const { clearTestVariation } = useTestVariationContext();

  return clearTestVariation;
};

const useSyncTestVariation = () => {
  const { syncTestVariation } = useTestVariationContext();

  return syncTestVariation;
};

const useSyncCookieMappedTestVariations = () => {
  const { syncCookieMappedTestVariations } = useTestVariationContext();

  return syncCookieMappedTestVariations;
};

export const useTestVariation = (localStorageKey, defaultValue) => {
  const syncTestVariation = useSyncTestVariation();
  const getTestVariation = useGetTestVariation();

  useEffect(() => {
    syncTestVariation(localStorageKey);
  }, [localStorageKey, syncTestVariation]);

  const variation = getTestVariation(localStorageKey);

  if (variation === undefined || variation === null || variation === '') {
    return defaultValue;
  }

  return variation;
};
